import { useId, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError, toApiError, unwrap, type AdminKommuneOut } from "../../api/client";
import { ExportTable } from "../../components/ExportTable";
import { exportDownloadUrl, usePollPendingExports } from "../../lib/exports";
import { ErrorMessage, LiveMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDateTime, formatNumber, runStatusLabel, runSummaryText } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

function RunCell({ k }: { k: AdminKommuneOut }) {
  const run = k.run;
  if (!run) return <span className="muted">{t("admin.noRun")}</span>;
  const summary = runSummaryText(run.summary);
  return (
    <>
      <span className={`run-status run-${run.status}`}>{runStatusLabel(run.status)}</span>{" "}
      {formatDateTime(run.finished_at ?? run.started_at)}
      {run.synthetic && <span className="badge badge-synthetic">{t("admin.synthetic")}</span>}
      {summary && <span className="cell-note">{summary}</span>}
    </>
  );
}

export function KommunerTab() {
  const id = useId();
  const kommuner = useQuery("admin:kommuner", (signal) =>
    unwrap(api.GET("/api/admin/kommuner", { signal })),
  );
  const demos = useQuery("admin:exports", (signal) =>
    unwrap(api.GET("/api/admin/exports", { signal })),
  );
  usePollPendingExports(demos.data, demos.reload);
  const [message, setMessage] = useState("");
  const [nummer, setNummer] = useState("");
  const [navn, setNavn] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[0-9]{4}$/.test(nummer.trim())) {
      setError(new ApiError(422, t("admin.nummerInvalid")));
      return;
    }
    setBusy("create");
    try {
      const k = await unwrap(
        api.POST("/api/admin/kommuner", { body: { nummer: nummer.trim(), navn: navn.trim() } }),
      );
      setNummer("");
      setNavn("");
      setMessage(t("admin.kommuneCreated", { nummer: k.nummer }));
      kommuner.reload();
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(null);
    }
  }

  async function trigger(nr: string) {
    setError(null);
    setBusy(nr);
    try {
      const res = await unwrap(
        api.POST("/api/admin/kommuner/{nummer}/runs", { params: { path: { nummer: nr } } }),
      );
      setMessage(t("admin.runQueued", { nummer: nr, job: res.job_id }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(null);
    }
  }

  async function demo(nr: string) {
    setError(null);
    setBusy(`demo:${nr}`);
    try {
      const item = await unwrap(
        api.POST("/api/admin/kommuner/{nummer}/exports", {
          params: { path: { nummer: nr } },
          body: { kind: "pdf" },
        }),
      );
      demos.setData((prev) => [item, ...(prev ?? []).filter((e) => e.id !== item.id)]);
      demos.reload();
      setMessage(t("admin.demoQueued", { nummer: nr }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <LiveMessage message={message} />
      {error && <ErrorMessage error={error} />}
      <form className="card" onSubmit={(e) => void create(e)} noValidate>
        <h2>{t("admin.kommuneCreateTitle")}</h2>
        <div className="form-row">
          <div className="field">
            <label htmlFor={`${id}-nummer`}>{t("admin.kommuneNummer")}</label>
            <input
              id={`${id}-nummer`}
              inputMode="numeric"
              maxLength={4}
              required
              className="input-short"
              aria-describedby={`${id}-hint`}
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
            />
            <span id={`${id}-hint`} className="hint">
              {t("admin.kommuneNummerHint")}
            </span>
          </div>
          <div className="field">
            <label htmlFor={`${id}-navn`}>{t("admin.kommuneNavn")}</label>
            <input
              id={`${id}-navn`}
              maxLength={100}
              value={navn}
              onChange={(e) => setNavn(e.target.value)}
            />
          </div>
          <div className="field field-action">
            <button type="submit" className="btn btn-primary" disabled={busy === "create"}>
              {t("admin.kommuneCreate")}
            </button>
          </div>
        </div>
      </form>

      <div className="toolbar">
        <button type="button" className="btn btn-secondary btn-small" onClick={kommuner.reload}>
          {t("admin.refresh")}
        </button>
      </div>
      {kommuner.error && <ErrorMessage error={kommuner.error} onRetry={kommuner.reload} />}
      {!kommuner.data && !kommuner.error && <Loading />}
      {kommuner.data && kommuner.data.length === 0 && <p>{t("admin.kommunerEmpty")}</p>}
      {kommuner.data && kommuner.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("admin.kommunerCaption")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("admin.colNummer")}</th>
                <th scope="col">{t("admin.colNavn")}</th>
                <th scope="col" className="num">
                  {t("admin.colOrgs")}
                </th>
                <th scope="col">{t("admin.colLatestRun")}</th>
                <th scope="col">{t("admin.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {kommuner.data.map((k) => (
                <tr key={k.nummer}>
                  <th scope="row">{k.nummer}</th>
                  <td>{k.navn || t("common.dash")}</td>
                  <td className="num">{formatNumber(k.orgs)}</td>
                  <td>
                    <RunCell k={k} />
                  </td>
                  <td>
                    <span className="link-group">
                      <button
                        type="button"
                        className="btn btn-primary btn-small"
                        disabled={busy !== null}
                        onClick={() => void trigger(k.nummer)}
                        aria-label={t("admin.triggerRunLabel", {
                          kommune: `${k.nummer} ${k.navn}`.trim(),
                        })}
                      >
                        {t("admin.triggerRun")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        disabled={busy !== null || !k.run || k.run.status !== "succeeded"}
                        onClick={() => void demo(k.nummer)}
                        aria-label={t("admin.demoPdfLabel", {
                          kommune: `${k.nummer} ${k.navn}`.trim(),
                        })}
                      >
                        {t("admin.demoPdf")}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <section className="section" aria-labelledby="demo-exports-heading">
        <h2 id="demo-exports-heading">{t("admin.demoTitle")}</h2>
        {demos.error && <ErrorMessage error={demos.error} onRetry={demos.reload} />}
        {!demos.data && !demos.error && <Loading />}
        {demos.data?.length === 0 && <p>{t("admin.demoEmpty")}</p>}
        {!!demos.data?.length && (
          <ExportTable
            exports={demos.data}
            caption={t("admin.demoTitle")}
            kommuneName={(nr) => kommuner.data?.find((k) => k.nummer === nr)?.navn || nr}
            schoolName={(schoolId) => schoolId}
            downloadHref={(e) => exportDownloadUrl.admin(e.id)}
          />
        )}
      </section>
    </div>
  );
}
